import { Suspense, lazy } from 'react';
import { Center, Loader } from '@mantine/core';
import { Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuth } from './features/shell/RequireAuth';
import { Shell } from './features/shell/Shell';

const PublicFormPage = lazy(() =>
  import('./features/public/PublicFormPage').then((m) => ({ default: m.PublicFormPage })),
);
const PublicServicePage = lazy(() =>
  import('./features/public/PublicServicePage').then((m) => ({ default: m.PublicServicePage })),
);
const MyServicesPage = lazy(() =>
  import('./features/user/MyServicesPage').then((m) => ({ default: m.MyServicesPage })),
);
const InstanceDetailPage = lazy(() =>
  import('./features/instances/InstanceDetailPage').then((m) => ({ default: m.InstanceDetailPage })),
);
const SearchPage = lazy(() => import('./features/search/SearchPage').then((m) => ({ default: m.SearchPage })));
const MembersPage = lazy(() => import('./features/admin/MembersPage').then((m) => ({ default: m.MembersPage })));
const FormsPage = lazy(() => import('./features/admin/FormsPage').then((m) => ({ default: m.FormsPage })));
const FormBuilderPage = lazy(() =>
  import('./features/admin/FormBuilderPage').then((m) => ({ default: m.FormBuilderPage })),
);
const ServicesPage = lazy(() => import('./features/admin/ServicesPage').then((m) => ({ default: m.ServicesPage })));
const ImportPage = lazy(() => import('./features/admin/ImportPage').then((m) => ({ default: m.ImportPage })));
const ServiceBuilderPage = lazy(() =>
  import('./features/admin/ServiceBuilderPage').then((m) => ({ default: m.ServiceBuilderPage })),
);
const InstancesPage = lazy(() =>
  import('./features/admin/InstancesPage').then((m) => ({ default: m.InstancesPage })),
);
const TemplatesPage = lazy(() =>
  import('./features/admin/TemplatesPage').then((m) => ({ default: m.TemplatesPage })),
);
const AuditPage = lazy(() => import('./features/admin/AuditPage').then((m) => ({ default: m.AuditPage })));
const AdminDashboard = lazy(() =>
  import('./features/admin/AdminDashboard').then((m) => ({ default: m.AdminDashboard })),
);
const PlatformTenantsPage = lazy(() =>
  import('./features/platform/PlatformTenantsPage').then((m) => ({ default: m.PlatformTenantsPage })),
);
const PlatformCatalogPage = lazy(() =>
  import('./features/platform/PlatformCatalogPage').then((m) => ({ default: m.PlatformCatalogPage })),
);
const PlatformAuditPage = lazy(() =>
  import('./features/platform/PlatformAuditPage').then((m) => ({ default: m.PlatformAuditPage })),
);
const PlatformFormBuilderPage = lazy(() =>
  import('./features/admin/FormBuilderPage').then((m) => ({
    default: () => <m.FormBuilderPage basePath="/api/v1/platform/forms" platform />,
  })),
);
const PlatformServiceBuilderPage = lazy(() =>
  import('./features/admin/ServiceBuilderPage').then((m) => ({
    default: () => (
      <m.ServiceBuilderPage basePath="/api/v1/platform/services" formsPath="/api/v1/platform/forms" platform />
    ),
  })),
);

const Loading = () => (
  <Center h="60vh">
    <Loader />
  </Center>
);

export function AppRoutes() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* the public client: bare page, no app shell, no auth provider usage */}
        <Route path="/public/forms/:token" element={<PublicFormPage />} />
        <Route path="/public/services/:token" element={<PublicServicePage />} />
        <Route path="/callback" element={<Loading />} />

        <Route
          path="/"
          element={
            <RequireAuth>
              <Shell />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/me/services" replace />} />
          <Route path="me/services" element={<MyServicesPage />} />
          <Route path="instances/:id" element={<InstanceDetailPage />} />
          <Route path="search" element={<SearchPage />} />

          <Route path="admin" element={<AdminDashboard />} />
          <Route path="admin/members" element={<MembersPage />} />
          <Route path="admin/forms" element={<FormsPage />} />
          <Route path="admin/forms/:formId" element={<FormBuilderPage />} />
          <Route path="admin/services" element={<ServicesPage />} />
          <Route path="admin/services/:serviceId" element={<ServiceBuilderPage />} />
          <Route path="admin/import" element={<ImportPage />} />
          <Route path="admin/instances" element={<InstancesPage />} />
          <Route path="admin/templates" element={<TemplatesPage />} />
          <Route path="admin/audit" element={<AuditPage />} />

          <Route path="platform/tenants" element={<PlatformTenantsPage />} />
          <Route path="platform/catalog" element={<PlatformCatalogPage />} />
          <Route path="platform/forms/:formId" element={<PlatformFormBuilderPage />} />
          <Route path="platform/services/:serviceId" element={<PlatformServiceBuilderPage />} />
          <Route path="platform/audit" element={<PlatformAuditPage />} />

          <Route path="*" element={<Navigate to="/me/services" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
